import { useState, useEffect, useMemo, useCallback } from "react";
import { api, apiBase } from "../api";
import type {
  ChangeRequestForMark,
  CrStatus,
  Identity,
} from "../types";
import Icon from "../components/Icon";

type Filter = CrStatus | "all";

type ChangeRequestsProps = {
  identity: Identity;
  onShowToast: (msg: string) => void;
};

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

export default function ChangeRequests({
  identity,
  onShowToast,
}: ChangeRequestsProps) {
  const [filter, setFilter] = useState<Filter>("pending");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [crs, setCrs] = useState<ChangeRequestForMark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDismissId, setConfirmDismissId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!apiBase()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listChangeRequests({
        status: filter,
        client_id: clientFilter === "all" ? undefined : clientFilter,
      });
      setCrs(res.change_requests);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filter, clientFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const cr of crs) seen.set(cr.client_id, cr.client_name);
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [crs]);

  async function markDone(cr: ChangeRequestForMark) {
    setBusyId(cr.id);
    try {
      await api.markChangeRequestDone(cr.id);
      if (filter === "pending") {
        setCrs((prev) => prev.filter((c) => c.id !== cr.id));
      } else {
        setCrs((prev) =>
          prev.map((c) =>
            c.id === cr.id
              ? { ...c, status: "done", done_at: new Date().toISOString() }
              : c
          )
        );
      }
      onShowToast("Marked done.");
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function dismiss(cr: ChangeRequestForMark) {
    setBusyId(cr.id);
    try {
      await api.deleteChangeRequest(cr.id);
      setCrs((prev) => prev.filter((c) => c.id !== cr.id));
      onShowToast("Dismissed.");
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
      setConfirmDismissId(null);
    }
  }

  return (
    <div className="page page-enter">
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div className="section-head">
          <div>
            <span className="eyebrow">Inbound from clients.</span>
            <h2 className="t-h2" style={{ marginTop: 10, fontSize: 32 }}>
              Change requests.
            </h2>
          </div>
        </div>

        <div className="filter-row" style={{ marginBottom: 20 }}>
          <div className="chip-filter">
            <button
              className={filter === "pending" ? "on" : ""}
              onClick={() => setFilter("pending")}
            >
              Pending{" "}
              {filter === "pending" && crs.length > 0 && (
                <span className="count">{crs.length}</span>
              )}
            </button>
            <button
              className={filter === "done" ? "on" : ""}
              onClick={() => setFilter("done")}
            >
              Done
            </button>
            <button
              className={filter === "all" ? "on" : ""}
              onClick={() => setFilter("all")}
            >
              All
            </button>
          </div>

          {clientOptions.length > 1 && (
            <div className="chip-filter" style={{ marginLeft: 8 }}>
              <button
                className={clientFilter === "all" ? "on" : ""}
                onClick={() => setClientFilter("all")}
              >
                All clients
              </button>
              {clientOptions.map((c) => (
                <button
                  key={c.id}
                  className={clientFilter === c.id ? "on" : ""}
                  onClick={() => setClientFilter(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          <div className="spacer" />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "var(--fg-subtle)",
            }}
          >
            Sorted by submitted
          </span>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(229,138,109,0.10)",
              border: "1px solid rgba(229,138,109,0.35)",
              borderRadius: "var(--r-md)",
              color: "#e58a6d",
              marginBottom: 20,
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Icon name="x" size={14} />
            {error}
          </div>
        )}

        {loading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="skeleton"
                style={{
                  height: 88,
                  marginBottom: 8,
                  borderRadius: "var(--r-md)",
                }}
              />
            ))}
          </div>
        ) : crs.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {crs.map((cr) => (
              <CrRow
                key={cr.id}
                cr={cr}
                busy={busyId === cr.id}
                confirmDismiss={confirmDismissId === cr.id}
                onMarkDone={() => void markDone(cr)}
                onAskDismiss={() => setConfirmDismissId(cr.id)}
                onCancelDismiss={() => setConfirmDismissId(null)}
                onDismiss={() => void dismiss(cr)}
              />
            ))}
          </div>
        )}

        {/* identity is intentionally received for future role-aware behavior */}
        <div hidden>{identity.email}</div>
      </div>
    </div>
  );
}

function CrRow({
  cr,
  busy,
  confirmDismiss,
  onMarkDone,
  onAskDismiss,
  onCancelDismiss,
  onDismiss,
}: {
  cr: ChangeRequestForMark;
  busy: boolean;
  confirmDismiss: boolean;
  onMarkDone: () => void;
  onAskDismiss: () => void;
  onCancelDismiss: () => void;
  onDismiss: () => void;
}) {
  const isDone = cr.status === "done";

  return (
    <div
      className="card card-pad"
      style={{
        padding: "16px 20px",
        opacity: isDone ? 0.6 : 1,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 520,
              color: "var(--fg)",
            }}
          >
            {cr.client_name}
          </span>
          <span style={{ color: "var(--fg-subtle)" }}>—</span>
          <a
            href={`#asset-${cr.asset_id}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(`#asset-${cr.asset_id}`, "_blank");
            }}
            style={{
              fontSize: 14,
              color: "var(--accent)",
              textDecoration: "none",
            }}
          >
            {cr.asset_name}
          </a>
        </div>

        <div style={{ fontSize: 13, color: "var(--fg)", marginBottom: 4 }}>
          {cr.op_type === "field_update" ? (
            <>
              <span style={{ color: "var(--fg-muted)" }}>
                {cr.field_label ?? cr.field_name}:
              </span>{" "}
              <strong>{cr.new_value ?? "—"}</strong>
            </>
          ) : (
            <>
              <span style={{ color: "var(--fg-muted)" }}>
                Image update for slot:
              </span>{" "}
              <strong>{cr.image_slot ?? "—"}</strong>
            </>
          )}
        </div>

        <div
          style={{
            fontSize: 12,
            color: "var(--fg-muted)",
            fontFamily: "var(--font-mono)",
          }}
        >
          Submitted by {cr.submitted_by_email} · {relTime(cr.submitted_at)}
          {isDone && cr.done_at && (
            <>
              {" · "}
              <span style={{ color: "var(--accent)" }}>
                Done {relTime(cr.done_at)}
              </span>
            </>
          )}
        </div>

        {cr.note && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              borderLeft: "2px solid var(--rule-strong)",
              background: "var(--surface-sunk)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--fg-muted)",
              borderRadius: 2,
            }}
          >
            “{cr.note}”
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {confirmDismiss ? (
          <>
            <span style={{ ...EYEBROW, color: "#e58a6d" }}>Dismiss?</span>
            <button
              className="btn btn-quiet btn-sm"
              onClick={onDismiss}
              disabled={busy}
              style={{ color: "#e58a6d", fontSize: 12 }}
            >
              {busy ? "…" : "Yes, dismiss"}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={onCancelDismiss}
              style={{ fontSize: 12 }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {!isDone && (
              <button
                className="btn btn-primary btn-sm"
                onClick={onMarkDone}
                disabled={busy}
              >
                {busy ? "…" : "Mark done"}
              </button>
            )}
            <button
              className="btn btn-quiet btn-sm"
              onClick={onAskDismiss}
              disabled={busy}
              style={{ color: "var(--fg-muted)", fontSize: 12 }}
            >
              <Icon name="trash" size={12} /> Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ filter }: { filter: Filter }) {
  if (filter === "pending") {
    return (
      <div className="empty card" style={{ padding: "80px 24px" }}>
        <div className="glyph">
          <Icon name="inbox" size={22} />
        </div>
        <h4 className="t-h4">Inbox zero.</h4>
        <p style={{ marginTop: 8 }}>
          No change requests waiting. When a client submits one, it lands here.
        </p>
      </div>
    );
  }
  return (
    <div className="empty card" style={{ padding: "80px 24px" }}>
      <div className="glyph">
        <Icon name="inbox" size={22} />
      </div>
      <h4 className="t-h4">No requests in this view.</h4>
      <p style={{ marginTop: 8 }}>Adjust the filters to see other states.</p>
    </div>
  );
}
